/* ═══════════════════════════════════════════════════════════════════════════════
   PREPAID CONTRACT MODAL — the ONE prepaid editor.
   ───────────────────────────────────────────────
   EXTRACTED VERBATIM from VendorDashboard.js (Build 3 c2). It was a private
   component there; the Step-6 wizard needs the same editor, and Principle 0 says
   the answer is ONE component with two importers, never a lookalike. The component
   body below is byte-identical to the version that shipped inside VendorDashboard —
   only its home changed.

   ★ IT IS PROP-PURE, which is what makes it embeddable: no useModel, no API call,
   no await, no markStale. Every write leaves through onSave / onGenerate / onDelete,
   so each host decides what "save" means — the Dashboard PUTs to the live API, the
   wizard mutates its in-memory step6Prepaid and lets the Step-6 import persist the
   whole set transactionally.

   PAY_FREQ and ML MOVED HERE rather than being copied: VendorDashboard now imports
   them from this module. That keeps the declaration count unchanged (CLAUDE.md §12
   already tracks the billing-frequency map at six copies and ML at nine — this
   commit adds no more). The home is pragmatic, not ideal; unifying either map is
   the separate open item.
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useState, useMemo, useRef } from "react";
import { addMonths, monthOffset, formatMonthLabel } from "../engines/dateUtils";
// ★ P3-B item 3 — `deriveAmortLag` added. This component was the LAST surface persisting
// the operator's TYPED lag; every other write site already derives it from the dict being
// written (AdminPage ×2, the shipped prepaidReconcile ×2). One rule, all callers.
import { buildCycleAmort, buildForecastTail, forecastWindowLength, deriveAmortLag, prepaidCycleRow } from "../engines/prepaidAmort";
// ★ THE SHARED account -> IS-leaf resolver. The modal used to spell this join itself;
// it is now the same function the engine (§12b), the Vendor Dashboard overlay and the
// Step-6 screen use, so the dropdown cannot disagree with what the model books.
import { buildPrepaidLabelToLine, resolvePrepaidContractLine } from "../engines/prepaidRouting";
import GuardedDialog from "./GuardedDialog";   // Batch 1b (L3641): the unsaved-changes guard (it calls useUnsavedChangesWarning)
import { compareNamesNaturally } from "../engines/payrollKeys";   // the vendor picker's order (alphabetised, natural)
import ViewOnly from "../components/ViewOnly";
import { prepaidRenewsDash } from "../utils/prepaidRenewals";   // prepaid renewals part 2: when a contract cannot renew ("—")
import { parseAmount } from "../utils/pasteRow";   // a typed or pasted money amount: $, commas, spaces

/* THE PREPAID AMOUNT BOX (2026-10-29): the raw text lives in amtDraft while the box is focused, so a typed "." survives the
 * keystroke. The box used to re-format its own number on every change, which dropped the decimal point -- typing "3,617.38"
 * gave 361,738 (pre-existing since 529c075, 2026-08-03). amountText keeps digits, commas and ONE point; amountValue reads it
 * to the cent through parseAmount (the paste owner, utils/pasteRow.js); fmtAmount is the box's display when not focused. */
const amountText = s => { const t = String(s == null ? "" : s).replace(/[^0-9.,]/g, ""); const i = t.indexOf("."); return i < 0 ? t : t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, ""); };
const amountValue = t => { const n = parseAmount(t); return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0; };
const fmtAmount = n => { const v = Number(n) || 0; return v.toLocaleString("en-US", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }); };

// Billing cadences. Exported — VendorDashboard imports this rather than keeping a copy.
export const PAY_FREQ = [
  { id: "monthly", label: "Monthly", months: 1 },
  { id: "quarterly", label: "Quarterly", months: 3 },
  { id: "semi_annual", label: "Semi-Annual", months: 6 },
  { id: "annual", label: "Annual", months: 12 },
];
// Month label. Exported for the same reason. Moved verbatim from VendorDashboard.
const r2 = n => Math.round(n * 100) / 100;

/* ★★ C1 s1 (sub-task H) — `saveMode` IS THE HOST'S DECLARATION OF WHAT ITS onSave DOES.
 * Same trap C3 closed for ForecastWizard, same remedy. This editor is prop-pure and has
 * TWO hosts that mean opposite things by "Save":
 *   · VendorDashboard  — onSave={savePrepaid} → live API + markStale. PERSISTS.
 *   · AdminPage Step 6 — onSave mutates the in-memory step6Prepaid array; the Step-6
 *     import persists the whole set later. LOCAL.
 * and it showed an identical "Save" / "Remove" / "Generate Series" in both.
 *
 * ★ WHAT WAS ALREADY HONEST, AND IS DELIBERATELY LEFT ALONE: this modal never claims
 * "Saved" — it has no success message at all (the host closes it), so unlike
 * ForecastWizard there is no false green assertion to retract. And it never clears its
 * own dirty flag: `isDirty` compares a useRef-frozen snapshot to `f`, and handleSave
 * does not reset that snapshot, so nothing here DISARMS the browser guard the way
 * ForecastWizard's dirty-clear did. The gaps were the VERBS and the absence of any
 * statement that a local save is not a save.
 *
 * ★ DEFAULT 'local', FAIL-SAFE — a host that forgets to declare gets the honest,
 * non-claiming behaviour; forgetting then costs an over-cautious label instead of the
 * operator's work. Both live hosts declare explicitly.
 * ★ The shared useUnsavedChangesWarning hook is NOT modified (4 consumers); only the
 * value handed to it is affected, exactly as C3 did. */
/* W-053 3b: canEdit -- may this login change the model? FROM THE HOST, like every other input (this component stays prop-pure):
   VendorDashboard passes useModel().canEditModel, AdminPage (Step 6) canEditModel(user). Default false: fail-closed. A false
   greys the form and Save / Apply / Generate / Remove (components/ViewOnly); Cancel stays live. */
/* Prepaid renewals part 2 (2026-10-29): `renewsSuggestion` = the host's engine suggestion for this contract ({ suggest, reason },
 * utils/prepaidRenewals prepaidRenewsView) -- shown while Renews is unanswered; the stored answer is the operator's. */
export default function PrepaidModal({ contract, prelinkVendor, vendors, isLineTaxonomy, allMonths, firstForecast, lastForecast, onSave, onGenerate, onDelete, onClose, error, saveMode = "local", canEdit = false, renewsSuggestion = null }) {
  const isLocal = saveMode !== "persist";
  const isNew = !contract;
  const monthOptions = (allMonths && allMonths.length) ? allMonths : [];
  // Account = GL account display label (same pool as the Vendor Modal IS-line
  // picker: isLineTaxonomy entry labels). Default = the linked vendor's is_line
  // label; overridable to any account.
  const acctLabelFor = (v) => (v && isLineTaxonomy?.byKey.get(v.isCat)?.label) || "";
  // SC-2 (slug-at-write): options carry the SLUG (is_line_key) as `value` + the
  // label as `display`, QUALIFIED by IS category when a label is shared by >=2
  // vendor-assignable entries (cid=3: "Professional Development (COGS)" vs
  // "(Revenue)", "Travel (COGS)" vs "(S&M)"). Storing the slug lets §12b route
  // unambiguously; `account` (the label) is still stored for display/legacy.
  const accountOptions = useMemo(() => {
    const entries = (isLineTaxonomy?.entries || []).filter(e => e.isVendorAssignable && e.id && e.label);
    const labelCount = {};
    entries.forEach(e => { labelCount[e.label] = (labelCount[e.label] || 0) + 1; });
    return entries
      .map(e => ({ value: e.id, label: e.label, display: labelCount[e.label] > 1 ? `${e.label} (${e.section})` : e.label }))
      .sort((a, b) => a.display.localeCompare(b.display));
  }, [isLineTaxonomy]);
  /* ★★ THE SHARED RESOLVER, NOT A PRIVATE LABEL MATCH (the rename-regression fix).
   * This used to filter `accountOptions` by `o.label === label` — a FOURTH spelling of
   * the account→leaf join that the engine (§12b), the Vendor Dashboard overlay and the
   * Step-6 screen all express through `resolvePrepaidContractLine`. It is now the same
   * function, so the dropdown cannot disagree with what the engine books.
   * ★ IT ALSO PREFERS THE STORED SLUG: a contract stamped by the backfill seeds from
   * `is_line` and never consults a label, which is what makes a rename harmless. An
   * unroutable row (the `_archive_*` contracts) still resolves to null and the dropdown
   * correctly shows its placeholder — that arm must keep working, not be papered over.
   * ⓘ The old copy matched against `accountOptions`, which is filtered to
   * `isVendorAssignable`; the shared map is built from every emitted leaf. The two have
   * never disagreed (0 ambiguous on any tenant) but they were free to, which is the §6
   * silent-drift shape this removes. */
  const lineDisplayBySlug = useMemo(() => new Map(accountOptions.map(o => [o.value, o.display])), [accountOptions]);
  const vendorLineDisplay = (v) => lineDisplayBySlug.get(v && v.isCat) || acctLabelFor(v) || "no IS line";   // U9
  const labelToLine = useMemo(
    () => buildPrepaidLabelToLine((isLineTaxonomy?.entries || []).map(e => ({ id: e.id, label: e.label }))),
    [isLineTaxonomy]);
  const resolveSlug = (c) => resolvePrepaidContractLine(
    { is_line: c && c.is_line, account: (c && c.account) || "" }, labelToLine).lineKey || "";

  const [f, setF] = useState(contract ? {
    vendorId: contract.vendorId ?? null,
    vendorName: contract.vendor || "",
    account: contract.account || "",
    // SC-2: prefer the stored slug; fall back to resolving the old label-only row.
    // (The resolver does that precedence itself — slug first, then the label map.)
    isLine: resolveSlug(contract),
    start: contract.start || (monthOptions[0] || ""),
    length: contract.length ?? null,
    indefinite: contract.length == null,
    prepaid: contract.prepaid || 0,
    eventIdentifier: contract.eventIdentifier || "",
    // cat-2 D1: event-month booking (Conferences). eventBooking opt-in derived
    // from a stored event_month; both NULL on every legacy row → inert.
    eventBooking: !!(contract && contract.eventMonth),
    eventMonth: contract?.eventMonth || "",
    // amort-lag: deferred amortization start, in months past start_month.
    amortLag: contract?.amortLagMonths ?? 0,
    // Prepaid renewals part 2: the Renews answer -- 'yes' / 'no' / '' (not answered).
    renews: contract.renews === true ? "yes" : contract.renews === false ? "no" : "",
    // SC-4 recurring fields (create-only; inert on edit) — kept for snapshot parity.
    recurring: false, freq: "quarterly", end: "",
    /* ★ C1 s3 · INC 2 — the grid's pinned forecast cells live IN `f`, deliberately.
     * `isDirty` is `JSON.stringify(f) !== initialSnapshot.current`, so holding the edits here
     * arms the unsaved-changes guard BY CONSTRUCTION — no new dirty logic, and no repeat of
     * C3's defect where a "save" silently disarmed the beforeunload listener. Step 6's own
     * page-level fingerprint already hashes `amort` with sorted keys (s2a paid for that in
     * advance). `null` = untouched; the payload never carries this field. */
    gridEdits: null,
  } : {
    // create — optionally pre-linked to a vendor (per-vendor "+ Add contract").
    vendorId: prelinkVendor?.id ?? null,
    vendorName: prelinkVendor?.name ?? "",
    account: prelinkVendor ? acctLabelFor(prelinkVendor) : "",
    // SC-2: default slug = the vendor's own is_line (v.isCat === is_line_key).
    isLine: prelinkVendor ? (prelinkVendor.isCat || "") : "",
    start: firstForecast || (monthOptions[monthOptions.length - 1] || ""),
    length: 12, indefinite: false, prepaid: 0, eventIdentifier: "",
    // cat-2 D1: event-month booking — opt-in, default off.
    eventBooking: false, eventMonth: "",
    // amort-lag: deferred amortization start, in months past start_month (default 0).
    amortLag: 0,
    renews: "",   // prepaid renewals part 2: a new contract starts unanswered
    // SC-4 recurring-series mode (create-only): one stored row per cycle.
    recurring: false, freq: "quarterly", end: "",
    gridEdits: null,   // INC 2 — see the edit-branch note above; inert on create (no saved grid)
  });
  const [regenRequested, setRegenRequested] = useState(false);
  // INC 2 — the cell currently being typed into (raw text, so a partial "-" or "1." is legal),
  // and the one-click acknowledgement of a non-conserving draft.
  const [cellDraft, setCellDraft] = useState(null);   // { m, text } | null
  const [amtDraft, setAmtDraft] = useState(null);     // the Prepaid Amount box's raw text while focused | null
  const [draftAffirmed, setDraftAffirmed] = useState(false);

  // Existing DB schedule (non-empty) — preserved on edit unless regenerated (Q2).
  const priorAmort = (contract && contract.amort && Object.keys(contract.amort).length > 0) ? contract.amort : null;

  // 5b-safety: useRef-frozen snapshot + isDirty + beforeunload (tab-X Chrome
  // caveat known/accepted — §12). Backdrop gated on !isDirty below.
  const initialSnapshot = useRef(null);
  if (initialSnapshot.current === null) initialSnapshot.current = JSON.stringify(f);
  const isDirty = useMemo(() => JSON.stringify(f) !== initialSnapshot.current, [f]);
  /* Batch 1b (2026-11-03, L3641): the unsaved-changes guard is components/GuardedDialog.jsx (it calls useUnsavedChangesWarning -- the one
     beforeunload guard); this dialog keeps its own footer note (note={null}). */

  const iS = { width:"100%",padding:"10px 12px",background:"#F0F1F5",border:"1px solid rgba(0,0,0,0.12)",borderRadius:"8px",color:"#1A1C2E",fontSize:"14px",fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box" };
  const lS = { display:"block",fontSize:"11px",fontWeight:600,color:"#6B7094",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px" };

  /* ★★ C1 s3 · INC 4 / D6 — LENGTH IS A NUMBER OF MONTHS, NOT A CADENCE.
   *
   * WHAT THIS REPLACED: a four-option dropdown, `PERIODS = [[1,"Monthly"],[3,"Quarterly"],
   * [6,"Semi-Annual"],[12,"Annual"]]`, bound to `length_months`. So "Monthly" did not mean
   * "bills monthly" — it wrote `length: 1`. A stored odd term survived only via `showOddLen`,
   * which injected it as a FIFTH option so opening and saving would not coerce it; the operator
   * could LEAVE a 4-month term but could never CREATE or CHANGE to one. **The field was safe
   * only while it was not used**, which is the definition of a trap, and it is live on three
   * FORECAST-BEARING rows per tenant (HUB (Cyber) len 2 lag 3, Harbor Compliance len 24, Abacus
   * len 23) where a coercion re-prices real expense.
   *
   * ★ THE ENGINE NEVER NEEDED THE FIX — THE WIDGET WAS WITHHOLDING THE CAPABILITY.
   * `buildCycleAmort` (prepaidAmort.js:32-46) takes `len` as a divisor and a loop bound and has
   * NO standard-vs-odd branch: 1/3/6/12 was privileged nowhere except the option list. That is
   * why D6 moves no stored number — asserted mechanically by `prepaidAmort.js` being
   * byte-identical in the A/B.
   *
   * ⇒ `PERIODS` survives ONLY as SHORTCUT BUTTON CAPTIONS, and as the caption under the input
   *   when the typed number happens to be one of them. The cadence word now describes the
   *   VALUE where it is true and is simply absent where it is not, instead of being the value's
   *   identity. `STD_LENS` and `showOddLen` are DELETED: with a free input there is no "odd"
   *   case to preserve, and a 4-month term stops being an exception.
   *
   * ⛔ THE FENCE — SINGLE-CONTRACT PATH ONLY. In recurring mode the Frequency dropdown REPLACES
   * this control (`f.recurring ? … : …`, so they never render together) and `handleGenerate`
   * deliberately sets `length_months = freqMo` — cadence and term ARE the same number there, by
   * design. A months input must never write `length_months` from a cadence, nor let a term
   * change alter `f.freq`. Asserted both ways in the A/B. */
  const PERIODS = [[1,"Monthly"],[3,"Quarterly"],[6,"Semi-Annual"],[12,"Annual"]];
  const LEN_MAX = 120;   // 10y — a generous typo-catch, NOT the horizon: Harbor Compliance's
                         // term legitimately ends 2024-01, past forecast_through 2023-12.
  const lenCaption = (PERIODS.find(([n]) => n === Number(f.length)) || [])[1] || "";

  // Event Name shown only for the "Conferences" per-event vendor, or when a value
  // already exists (never hide a populated value on edit).
  /* ★ C1 s2b (B1, decision ii) — THE EVENT NAME FIELD FOLLOWS THE CHECKBOX, NOT THE
   * VENDOR NAME. This read `f.vendorName === "Conferences" || contract.eventIdentifier`
   * — a literal vendor-name test whose second clause is dead (`event_identifier` is NULL
   * on every row of both tenants). So the NAME field was Conferences-only while the
   * event-booking CHECKBOX was already un-gated for every vendor: the mechanic was
   * available to all, the label for it was not. Keying on the checkbox is the honest
   * rule — the field is meaningless unless the booking IS an event — and it broadens to
   * any prepaid vendor without inventing a second gate.
   * ⓘ `|| contract.eventIdentifier` is retained so a stored value is never hidden on
   * open (the original intent of that clause), even though no live row exercises it. */
  const showEvent = f.eventBooking || !!(contract && contract.eventIdentifier);
  /* Prepaid renewals part 2: can THIS contract (as the form now stands) renew? The same rule the contract lists use
   * (prepaidRenewsDash): an event booking, an indefinite or one-month contract, a zero amount -> no Renews answer. */
  const renewsDash = prepaidRenewsDash({
    vendor_name: f.vendorName, start_month: f.start, length_months: f.indefinite ? null : Number(f.length),
    prepaid_amount: Number(f.prepaid) || 0, event_identifier: (showEvent && f.eventIdentifier.trim()) ? f.eventIdentifier.trim() : null,
    event_month: f.eventBooking ? (f.eventMonth || "event") : null,
  });

  // Key fields that drive the schedule. Changing them on a contract that ALREADY
  // has a schedule surfaces a non-blocking "Regenerate?" notice (Q2); default is
  // PRESERVE the entered/imported schedule (historical given-data is protected).
  const keyChanged = !!priorAmort && !f.indefinite && (
    f.start !== contract.start || f.length !== contract.length || Number(f.prepaid) !== Number(contract.prepaid)
  );
  // amort-lag changed on an existing contract → forecast-only re-amort (below),
  // distinct from keyChanged's full rebuild. Preserves all closed/historical cells.
  const lagChanged = !!priorAmort && !f.indefinite && Number(f.amortLag) !== Number(contract.amortLagMonths ?? 0);

  /* ★ C1 hardening (CHANGE 5) — THE NAMED "TERM ENDED" STATE.
   * The primitive now PRESERVES the schedule when the amortization window is empty (the term
   * ended before the forecast begins) instead of returning `{}` and destroying it. That is the
   * fix — but without a notice it turns Regenerate and the lag box into a SILENT NO-OP, and a
   * silent no-op is the same failure shape as a silent wipe: the operator cannot tell whether
   * anything happened (§12.5 rule 7 — fail-loud means a NAMED, HANDLED state).
   * ★ IT READS THE PRIMITIVE'S OWN RULE. `forecastWindowLength` was extracted from
   * `buildForecastTail`'s body precisely so this notice cannot answer the question differently
   * from the function whose behaviour it is describing — the alternative was a second copy of
   * the n-arithmetic in the component, which is the drift this codebase keeps paying for.
   * Shown only when a generator was actually REQUESTED, so a row simply sitting past its term
   * says nothing. */
  const windowEmpty = !f.indefinite && !!f.start && !!firstForecast
    && Number.isFinite(Number(f.length))
    && !(forecastWindowLength(f.start, f.length, Number(f.amortLag) || 0, firstForecast) > 0);
  const termEndedNoop = !!priorAmort && windowEmpty && (regenRequested || lagChanged);
  /* Batch 1b (2026-11-03, L3628): THE LAG THAT IS SAVED IS THE SCHEDULE'S OWN (handleSave stores deriveAmortLag of the schedule it
   * writes), not this box -- the box only feeds a Regenerate or a lag change, which rebuild the forecast from start + lag. When that
   * rebuild would move where the schedule ENDS, say so before the save (diffs/step6-save-check.txt W2.2: a contract whose history began
   * late reads lag 2, and Regenerate moved its last month past the end the operator set). The SAME primitive and arguments the save
   * uses (buildForecastTail), run for display only -- nothing here reaches f or the payload. */
  const lastAmortMonth = a => Object.keys(a || {}).filter(m => Number(a[m]) !== 0).sort().pop() || null;
  const regenEndShift = (() => {
    if (!priorAmort || f.indefinite || termEndedNoop || !(regenRequested || lagChanged) || !firstForecast) return null;
    const lagN = Number(f.amortLag) || 0;
    const was = lastAmortMonth(priorAmort), will = lastAmortMonth(buildForecastTail(priorAmort, f.prepaid, f.start, f.length, lagN, firstForecast));
    return was && will && was !== will ? { was, will, lag: lagN } : null;
  })();

  /* ══════════════ S3-GRID-DERIVATION-BEGIN ══════════════
   * ★★ C1 s3 · GRID INCREMENT 1 — THE READ-ONLY PER-MONTH SCHEDULE + THE Σ READOUT.
   *
   * DISPLAY-ONLY, AND DELIBERATELY SO. Nothing below reaches `f`, `handleSave` or the
   * onSave payload — all six write branches (B1-B6) are byte-identical to HEAD, asserted
   * mechanically in the verify. This increment exists to (a) put the contract's stored
   * schedule in front of the operator at all, which no surface has ever done (the Vendor
   * Dashboard tab shows it per-vendor but the WIZARD shows only a one-line summary string),
   * and (b) give the §12.5-shaped reader-less `console.warn` at :305-312 its first visible
   * reader — a warning with no reader is not a check.
   *
   * ⛔ INCREMENT 1 SAID: "IT DESCRIBES THE **SAVED** SCHEDULE, NOT A PREVIEW." ★ INCREMENT 2
   * NARROWS THAT, IT DOES NOT ABANDON IT. With no grid edit the panel still shows exactly the
   * saved dict and says so. The moment the operator edits a forecast cell it shows the DRAFT —
   * because at that point showing the stale saved values would be the very defect s1 fixed (a
   * signal describing something other than what Save will write). The panel's header text
   * switches with it, so the screen never claims to be showing one thing while showing another.
   * ⓘ The conservation target and the residual now read `f.prepaid` (the amount that WILL be
   * saved) rather than the stored `contract.prepaid`. With an untouched form the two are the
   * same value and the panel is unmoved — proven over 510 rows in the Increment-2 A/B. With a
   * PENDING amount change they differ, and targeting the stored one would let "change the
   * amount, edit a cell, save" write a schedule that silently conserves to a number that is no
   * longer on the row. That is the silent class this arc exists to close.
   *
   * THE THREE MECHANICS THAT COULD EACH HIDE A REAL CELL, and why each is what it is:
   *  (i) COLUMNS ARE THE **UNION** of the dict's own key span and the contract's term span
   *      (payment month → start + lag + length − 1, a superset of both readings of "term").
   *      MEASURED: the stored dict legitimately EXCEEDS the term — Clever (cid=3 id 5563) is
   *      length_months=4 with FIVE cells. A term-scoped column set would render four of them
   *      and silently drop the fifth. The union is the only rule that cannot hide a cell.
   *  (ii) EMPTY ≠ ZERO. `val()` returns `null` for an absent key and a NUMBER for a stored
   *      one, INCLUDING 0. The Dashboard tab cannot do this (`a!==0?fmt(a):""` renders both
   *      as blank); §12.5 B3-9's rule — a zero is data — applies verbatim.
   *  (iii) NEGATIVES ARE REAL and are rendered as such: Servicenow −1,172.07 and
   *      Salesforce.com marketing −3,053.50 are live credit memos, two rows per tenant.
   *
   * THE Σ READOUT AND ITS TWO REGIMES (Phase 0 §B6). A residual is NOT automatically a
   * fault: 24-28 rows per tenant of correct, oracle-matching, imported data carry one. What
   * separates them is WHERE the schedule sits — a residual on a contract with no forecast
   * cell is a general-ledger fact and washes out at the seam (the BS re-seats from
   * `bs_actuals[cutoff]`); a residual on a contract that books forward reaches the forecast
   * balance sheet and never washes out. So `regime` is derived from the presence of a
   * forecast cell, not from the size of the number, and the benign case is worded as a
   * statement of fact rather than a warning — a check that always fires is a check nobody
   * reads. Surfaced always; blocking never. */
  const fmtCell = n => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const gHead = { padding:"5px 8px",fontSize:"9px",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"center",background:"#F0F1F5",borderBottom:"1px solid rgba(0,0,0,0.08)",whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif" };
  const gCell = { padding:"5px 8px",fontSize:"11px",fontFamily:"'Space Mono',monospace",textAlign:"right",whiteSpace:"nowrap" };
  const gLbl = { color:"#8B8FA3",textTransform:"uppercase",letterSpacing:"0.4px",fontSize:"9px",fontFamily:"'DM Sans',sans-serif",marginRight:"5px" };
  const gInp = { width:"78px",padding:"3px 5px",fontSize:"11px",fontFamily:"'Space Mono',monospace",textAlign:"right",border:"1px solid rgba(0,0,0,0.15)",borderRadius:"5px",background:"#FFFFFF",color:"#1A1C2E",outline:"none",boxSizing:"border-box" };

  /* ★★ C1 s3 · GRID INCREMENT 2 — CONSERVE-BY-DEFAULT (Phase 0 D4, option S-a made VISIBLE).
   *
   * THE RULE, stated once: an edited cell is PINNED at the operator's number; the still-unpinned
   * FORECAST cells of the saved schedule re-spread straight-line so the whole schedule sums to
   * the prepaid amount. Only forecast cells ever absorb — closed months are recorded history and
   * are never redistributed into.
   *
   * ★★ INCREMENT 3 — CLOSED CELLS ARE EDITABLE TOO, AND THE SAFETY IS A PROPERTY OF THE ENGINE,
   * NOT OF THIS COMPONENT. Editing a closed month records what the general ledger actually
   * booked. It CANNOT change the closed income statement, balance sheet or cash flow, because
   * three unconditional cutoff gates stand in front of them and none of them consults this dict:
   *     computeIS.js:33/41/49  `const pool = m <= cutoff ? actualsData : forecastData;`
   *     computeBS.js:64        `if (m <= cutoff) { result[line.id][m] = r2(A[line.id]?.[m] || 0); return; }`
   *     runFullModel.js:637    `if (m <= cutoff) return;   // FORECAST ONLY`
   * (and computeCFS reads bsData, so it inherits the BS gate). Those four lines are asserted
   * byte-unmodified in the Increment-3 A/B — the claim is re-checked mechanically every run
   * rather than trusted from this comment.
   * ⓘ CORROBORATED BY A NATURAL EXPERIMENT WE DID NOT HAVE TO BUILD: cid=3 and cid=8 store
   * **$2,344.14 of DIFFERENT closed amort cells** (Servicenow (Lightstep)) and their ver6
   * browser tie-out is $0.10 across 5,040 cells — that $0.10 being a FORECAST cell (HUB
   * (Cyber)). Closed divergence → $0.00 of statement divergence; forecast divergence → exactly
   * the golden's $0.10. A two-sided control, run by the data.
   * ⚠ ONE NAMED CONDITION (Phase 0 §B4): this holds because `bs_actuals.prepaid_expenses[cutoff]`
   * exists and the roll RE-SEATS from it at the seam. MEASURED present on all four tenants
   * ($435,340.70 on cid 3/8/40). A future tenant WITHOUT that anchor would have its forecast BS
   * roll forward from the closed cells directly, and closed edits would move a number. Flagged,
   * not fixed — nothing in code enforces the anchor's presence today.
   *
   * ⇒ SO WHAT A CLOSED EDIT *DOES* DO: it changes `closedSum`, which changes `remaining`, which
   * RE-PRICES THE FORECAST. That is the whole of its effect on the model, it is immediate and
   * visible in the grid, and the panel says so in words.
   *
   * ⛔ THIS IS NOT A LOOKALIKE OF `buildForecastTail`, AND THE DISTINCTION IS DELIBERATE
   * (Principle 0 demands the question be asked, not that every similar-looking thing be
   * merged). `buildForecastTail` spreads the remaining balance over the TERM WINDOW derived
   * from start/lag/length — it has no notion of a cell the operator has pinned, and it would
   * happily overwrite one. This spreads over THE UNPINNED SUBSET of the schedule's own
   * forecast months. Different domain, different rule. The two never run on the same save:
   * the grid draft short-circuits the branch chain (B0 below), and with no edits there is no
   * draft and `buildForecastTail` keeps every path it owns today.
   *
   * ★ THE UNPINNED SET IS THE SCHEDULE'S OWN FORECAST MONTHS, NOT EVERY FORECAST COLUMN.
   * Spreading across all rendered columns would INVENT bookings in months the contract does
   * not amortize in — a silent re-shaping of the schedule as a side effect of touching one
   * cell. Preserving the shape is the conservative reading, and the operator can still create
   * a month explicitly by typing into it (which pins it).
   *
   * ★ WHEN CONSERVATION IS IMPOSSIBLE IT DOES NOT FAKE IT. With no unpinned forecast month
   * left to absorb the balance, the residual simply stands — Increment 1.1's readout already
   * colours it and names it, and the save takes ONE explicit affirmation. It is never blocked
   * and never silently "corrected". */
  const applyCellEdits = (base, edits, ff, target) => {
    const out = {};
    const isFc = m => !!(ff && m >= ff);
    /* 1. CLOSED cells. ★ INCREMENT 3 — these are now EDITABLE, so a pinned closed month takes
     *    the operator's value and an unpinned one keeps the stored value. A CLEARED closed cell
     *    deletes its key (empty ≠ zero, §12.5 B3-9). Everything else about the closed half is
     *    unchanged: it still never reaches a closed statement (see the standing rule below). */
    for (const [m, v] of Object.entries(base)) {
      if (isFc(m)) continue;
      if (Object.prototype.hasOwnProperty.call(edits, m)) continue;   // pinned — written below
      out[m] = Number(v) || 0;
    }
    for (const [m, v] of Object.entries(edits)) {
      if (isFc(m)) continue;
      if (v === null) continue;                                       // cleared → no key at all
      out[m] = r2(Number(v) || 0);
    }
    // ★ THE COUPLING, MADE LIVE. `closedSum` is now the EDITED closed total, and it feeds
    // `remaining` below — so editing a closed month RE-PRICES THE FORECAST IMMEDIATELY, in the
    // grid, as the operator types. Phase 0 §B7 option S-a: the coupling has always existed at
    // edit time (`buildForecastTail` computes `remaining = prepaid − Σ(closed)` on every
    // generator run), it was simply INVISIBLE until now — deferred to whenever a generator
    // happened to fire next. Increment 3 does not introduce the coupling; it stops hiding it.
    const closedSum = r2(Object.values(out).reduce((s, v) => s + v, 0));
    // 2. PINNED forecast cells: the operator's number, verbatim. `null` = the cell was
    //    cleared, which is a pin too — it means "no entry here", not "re-spread into me".
    const pinnedMonths = [];
    for (const [m, v] of Object.entries(edits)) {
      if (!isFc(m)) continue;                      // closed months were handled above
      if (v === null) continue;                    // cleared → no key at all
      out[m] = r2(Number(v) || 0);
      pinnedMonths.push(m);
    }
    const pinnedSum = r2(pinnedMonths.reduce((s, m) => s + out[m], 0));
    // 3. The UNPINNED forecast months of the SAVED schedule absorb the balance.
    const unpinned = Object.keys(base)
      .filter(m => isFc(m))
      .filter(m => !Object.prototype.hasOwnProperty.call(edits, m))
      .sort();
    const remaining = r2(target - closedSum - pinnedSum);
    if (unpinned.length) {
      // §5: r2 at every step, and the LAST cell takes the remainder so the sum cannot drift.
      const per = r2(remaining / unpinned.length);
      let acc = 0;
      unpinned.forEach((m, i) => {
        const v = (i === unpinned.length - 1) ? r2(remaining - acc) : per;
        acc = r2(acc + v);
        out[m] = v;
      });
    }
    return out;
  };
  // null = the grid has not been touched. An empty edits object is also treated as untouched,
  // so clearing every edit returns the row to branch B6 (`amort = priorAmort`) exactly.
  const gridEdits = (f.gridEdits && Object.keys(f.gridEdits).length) ? f.gridEdits : null;
  const draftAmort = useMemo(
    () => (gridEdits ? applyCellEdits(priorAmort || {}, gridEdits, firstForecast, r2(Number(f.prepaid) || 0)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridEdits, priorAmort, firstForecast, f.prepaid]
  );

  const sched = useMemo(() => {
    const saved = priorAmort || {};
    // The grid renders the DRAFT when one exists, the SAVED dict otherwise.
    const dict = draftAmort || saved;
    const keys = Object.keys(saved);
    // (i) UNION: the dict's own keys ∪ the contract's term span. Term span runs from the
    // payment month through start + lag + length − 1 so it covers the lagged window too.
    // ★ Increment 2 widens the key half of the union to saved ∪ draft, so a month the
    // operator CLEARED keeps its column (they must be able to type into it again) and a month
    // they created appears. The `keys.length` gate stays on the SAVED dict: a contract with no
    // saved schedule still shows the note rather than an empty editable grid (see the filed
    // limitation in the Increment-2 verify — creating a schedule from nothing is not in scope).
    const set = new Set([...keys, ...Object.keys(dict)]);
    if (contract && keys.length) {
      const st = contract.start;
      const len = Number(contract.length);
      if (st && Number.isFinite(len) && len > 0) {
        const lag = Number(contract.amortLagMonths) || 0;
        for (let i = 0; i < lag + len; i++) set.add(addMonths(st, i));
      } else if (st) set.add(st);
    }
    // "YYYY-MM" sorts lexicographically == chronologically (the same property that makes
    // the jsonb key-order round-trip safe, measured in Phase 0 §A4.iv).
    const months = keys.length ? Array.from(set).sort() : [];
    const seamOn = !!firstForecast;
    const closed = seamOn ? months.filter(m => m < firstForecast) : [];
    const fc = seamOn ? months.filter(m => m >= firstForecast) : months;
    // (ii) EMPTY vs ZERO — null means "no key stored", 0 means "a zero is stored".
    const val = m => (Object.prototype.hasOwnProperty.call(dict, m) ? (Number(dict[m]) || 0) : null);
    const sum = ms => r2(ms.reduce((s, m) => s + (val(m) || 0), 0));
    const closedSum = sum(closed);
    const fcSum = sum(fc);
    const total = r2(closedSum + fcSum);
    // ★ Inc 2: the amount that WILL be saved, not the one on disk. Identical while the form is
    // untouched (`f.prepaid` is seeded from `contract.prepaid`); see the header comment for why.
    const prepaid = r2(Number(f.prepaid) || 0);
    const residual = r2(prepaid - total);
    // ★ Inc 2: read the EFFECTIVE dict, not the saved one — a forecast month the operator has
    // just created must count, or the clause would say "recorded history" about a live edit.
    const hasForecastCell = seamOn && Object.keys(dict).some(m => m >= firstForecast);
    /* ★★ C1 s3 · INCREMENT 1.1 — THE RESIDUAL'S PROMINENCE IS DRIVEN BY ITS MAGNITUDE,
     * AND THE CLOSED-vs-FORECAST DISTINCTION SURVIVES ONLY IN THE WORDING.
     *
     * INCREMENT 1 COLOURED BY REGIME: a residual confined to closed months was grey
     * whatever its size, on the §B6 reasoning that it is a general-ledger fact rather than
     * a modelling error. That reasoning is still TRUE and is still said, in words. But it
     * made the colour answer the wrong question. MEASURED on the live data: Clever
     * (cid=3 id 5563) is out by **$1,050.00** — a bookkeeper amortized a whole extra month
     * on a $4,200 contract — and it rendered in the same calm grey as a $0.02 remainder,
     * because both are closed-only. The operator scanning the panel could not tell a
     * material booking error from rounding noise, which is exactly the thing the readout
     * exists to tell them.
     *
     * ⇒ COLOUR ANSWERS "HOW BIG?", THE SENTENCE ANSWERS "WHERE?". They are different
     *   questions and were being answered by one channel.
     *
     * THE BOUNDARY IS EXACT, not approximate:
     *     residual === 0            → TIE      green
     *     0 < |residual| <= 1.00    → ROUNDING amber
     *          |residual| >  1.00   → MATERIAL red + bold
     *
     * ★ `=== 0` IS AN EXACT TEST AND IT IS SAFE — the $0.02 tolerance is DELIBERATELY GONE,
     *   not silently retained. `residual` is already `r2(prepaid - total)` of two r2'd
     *   inputs, so r2 has absorbed every sub-half-cent float artefact before this line: r2
     *   returns k/100 for an integer k, and k === 0 is exact (JS `-0 === 0` is true, so a
     *   negative-zero rounding cannot leak through either). No epsilon is needed and adding
     *   one would only re-hide the cent-level residuals we now want to show as amber.
     *   ⓘ THIS IS A DELIBERATE DIVERGENCE FROM THE $0.02 CONSERVATION GUARD AT :305, which
     *   keeps its own threshold for a different job: that guard fires a console line at
     *   SAVE time and must not cry wolf on remainder placement. This panel is passive — it
     *   colours, it never blocks — so it can afford to be exact, and an operator IS entitled
     *   to see a $0.01 residual on their own schedule.
     *
     * ⛔ `regime` AND `ties` ARE REMOVED RATHER THAN LEFT BESIDE `severity`. Keeping the old
     *   fields would leave two answers to "is this row fine?" in one object — the second
     *   source of truth this codebase keeps paying for. `hasForecastCell` survives because
     *   it now has exactly one job: picking the trailing clause. */
    const RESIDUAL_MATERIAL = 1.00;   // dollars — above this the residual is not rounding
    const absRes = Math.abs(residual);
    const severity = residual === 0 ? "tie" : (absRes <= RESIDUAL_MATERIAL ? "rounding" : "material");
    const resColor = severity === "tie" ? "#27AE60" : (severity === "rounding" ? "#8A6D00" : "#C0392B");
    const resBold = severity === "material";
    const msgColor = severity === "tie" ? "#6B7094" : resColor;
    /* THE CLAUSE — the §B6 nuance, kept in the sentence so a loud red number sitting on pure
     * history does not read as a broken model. A material closed-only residual IS worth the
     * operator's attention (it is a real booking error) but it is NOT a forecast defect, and
     * the sentence is where that difference belongs. */
    const clause = hasForecastCell
      ? ", and this reaches the forecast balance sheet."
      : ". Recorded history; the closed statements are unaffected.";
    /* ⓘ The DIRECTION is not repeated in words: the residual is rendered signed, immediately
     * above this sentence, so "-1,050.00" already says the schedule books MORE than the
     * payment. Saying it twice was the Increment-1 wording and it crowded out the magnitude. */
    const residualMsg = severity === "tie"
      ? "The schedule sums to the prepaid amount."
      : `Off by $${fmtCell(absRes)} — ${severity === "material" ? "worth a look" : "likely rounding"}${clause}`;
    // The seam divider sits on the FIRST forecast column, and only when both halves exist.
    const seamIdx = (seamOn && closed.length > 0 && fc.length > 0) ? closed.length : -1;
    // Inc 2: `pinned` drives the per-cell marker; `editable` is the forecast half only.
    // Inc 3: every edited month is pinned, closed or forecast (Inc 2 filtered to forecast only).
    const pinned = new Set(gridEdits ? Object.keys(gridEdits) : []);
    const pinnedClosed = seamOn ? [...pinned].filter(m => m < firstForecast).length : 0;
    return { dict, months, seamOn, closed, fc, val, closedSum, fcSum, total, prepaid, residual, hasForecastCell, severity, resColor, resBold, msgColor, clause, residualMsg, seamIdx, pinned, pinnedClosed, isDraft: !!draftAmort };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorAmort, contract, firstForecast, draftAmort, gridEdits, f.prepaid]);

  /* ★ THE AFFIRM GATE (Phase 0 D4 — BREAK-ON-AFFIRM). It fires only when a grid draft is
   * ACTIVE and cannot conserve, i.e. the operator has pinned every forecast cell and their
   * numbers do not sum to the amount. It is an ACKNOWLEDGEMENT, not a validation: one click,
   * always available, never refuses the save, and the residual it names is already on screen
   * in Increment 1.1's colours. A pre-existing residual on an UNEDITED row never trips it —
   * `draftAmort` is null there, so the gate cannot fire on the 48 gl_fact rows that carry a
   * legitimate GL residual (a check that fires on correct data is a check nobody reads). */
  const draftBreaks = !!draftAmort && sched.residual !== 0;
  /* ══════════════ S3-GRID-DERIVATION-END ══════════════ */

  const toggleIndefinite = (checked) => {
    if (checked) {
      // Q3: clearing an existing schedule needs a confirm.
      if (priorAmort && !window.confirm("This will remove the amortization schedule and park the full amount on the balance sheet — continue?")) return;
      setF(p => ({ ...p, indefinite: true, length: null }));
    } else {
      setF(p => ({ ...p, indefinite: false, length: contract?.length ?? 12 }));
    }
  };

  // Vendor required on CREATE; on EDIT of a pre-existing (possibly orphan) row,
  // relink is optional so the operator can just fix a field.
  const vendorOk = isNew ? (f.vendorId != null) : true;
  // SC-2: gate on the slug (f.isLine) rather than the label — an old ambiguous
  // row opens unselected, forcing the operator to pick (which stamps the slug).
  const requiredOk = vendorOk && f.isLine && f.start && (f.indefinite || (Number.isFinite(Number(f.length)) && Number(f.length) > 0));

  /* ★ INC 2 — COMMIT ONE CELL. NumInput semantics (§5): the field is `type="text"` with
   * `inputMode="decimal"`, the raw text lives in `cellDraft` while focused, and the value is
   * parsed on blur. NEGATIVES ARE PRESERVED — the sanitiser keeps `-` and `.`, unlike the
   * Prepaid Amount input's `[^0-9.]` strip, which would silently turn a credit into a charge
   * (measured: 4 live rows carry negative cells). EMPTY = delete the key (`null`, still a pin);
   * a typed 0 = store 0 (§12.5 B3-9 — a zero is data, and the grid can already render it). */
  const commitCell = (m, text) => {
    const t = String(text == null ? "" : text).trim();
    setF(p => {
      const next = { ...(p.gridEdits || {}) };
      if (t === "") next[m] = null;
      else {
        const n = parseFloat(t.replace(/[^0-9.-]/g, ""));   // hyphen LAST = literal; the minus survives
        next[m] = Number.isFinite(n) ? r2(n) : null;
      }
      return { ...p, gridEdits: next };
    });
    setCellDraft(null);
    setDraftAffirmed(false);   // any new edit retires a prior affirmation — re-affirm knowingly
  };
  const clearGridEdits = () => { setF(p => ({ ...p, gridEdits: null })); setCellDraft(null); setDraftAffirmed(false); };

  const handleSave = () => {
    // amort: indefinite → {}; create or previously-empty or explicit regen →
    // straight-line from the lagged start; lag-changed → forecast-only re-amort;
    // otherwise preserve the existing schedule (Q2).
    const lag = Number(f.amortLag) || 0;
    let amort;
    /* ★ C1 hardening (CHANGE 4) — WHICH generator produced this dict, or null when none did.
     * The Σ-conservation guard used to live INSIDE the `lagChanged` arm, so it watched ONE of
     * the three branches that route through `buildForecastTail` — and the two it did not watch
     * (event, Regenerate) are exactly the two that were silently destroying the Conferences
     * bookings. The guard now sits after the chain and fires for all three.
     * ⛔ It must NOT fire for the non-generator arms: `indefinite` empties the dict on purpose,
     * and `preserve` hands back a stored schedule whose residual describes the DATABASE, not
     * this edit — 24-28 rows per tenant carry one, so warning there would make the guard fire
     * on every no-op save of a correct row (a check that always fires is a check nobody reads).
     * Tracking the SOURCE rather than testing the value is what keeps that distinction. */
    let reamortVia = null;
    // cat-2 D1: event-month booking — full amount in the chosen event month
    // (single-key dict; the engine parks it on the BS until then, books it there).
    // Gated on BOTH the explicit opt-in checkbox AND a chosen month → regular
    // prepaids (eventBooking false) fall through to the unchanged chain below.
    /* ★★ C1 s2b (defect 5 + B5) — THE EVENT BRANCH, UNIFIED AND HISTORY-SAFE.
     *
     * WHAT THIS USED TO DO — one line, and it was the worst defect in the editor:
     *     if (f.eventBooking && f.eventMonth) amort = { [f.eventMonth]: r2(prepaid) };
     * It REPLACED THE ENTIRE DICT, unconditionally, with no awareness of the close
     * month. Tick the checkbox and pick a month on a row already booked in 2022 and
     * every closed cell was discarded and the whole amount re-placed in the chosen
     * month — a CLOSED booking silently moved into the forecast. Blast surface,
     * measured: 96 historical-only + 35 straddling contracts per tenant. Branches 4 and
     * 5 protect closed cells; this one did not, and it fired on a checkbox plus a
     * dropdown pick.
     *
     * ★ B5 — THE UNIFIED FRAME, IN CODE. An event booking is not a special kind of
     * schedule, it is the general one with length = 1: deposit at `start`, begin
     * amortizing after `lag`, spread over `length`. So this stops hand-building a dict
     * and routes through the SAME preserve-then-forecast primitive branch 4 uses.
     *   lag    = monthOffset(start → eventMonth)   (dateUtils' own helper, REUSED)
     *   length = 1
     * `buildForecastTail` then keeps every cell < firstForecast, spreads the REMAINING
     * balance over the single target month, and guarantees Σ(amort) == prepaid.
     * ⛔ THE STORED COLUMNS DO NOT MOVE. `event_month` is still written below and is
     * still the marker `isPrepaidEventStyle` keys on (→ deriveAmortLag 0 →
     * fillForecastPrepaidAmort declines to generate). Only the DICT CONSTRUCTION
     * unifies. Collapsing the column into amort_lag_months would flip 19 rows per
     * tenant into the generated class — the trap C1 Phase 0 flagged as #4.
     *
     * ⛔ AND IT REFUSES TO REWRITE CLOSED HISTORY SILENTLY. An event month at or before
     * the close month is a request to move a booking into a closed period. It takes an
     * explicit confirm, and even when confirmed it still goes through the preserve
     * primitive rather than the old wipe — so a confirmed edit re-points the FORECAST,
     * it never silently deletes what is already booked. Cancelling returns `null`, which
     * the caller treats as "operator declined" and abandons the save untouched. */
    /* ★★ INC 2 · BRANCH B0 — THE GRID DRAFT WINS, AND THAT PRECEDENCE IS DECLARED, NOT IMPLIED.
     * If the operator has pinned a forecast cell AND ALSO changed the lag / pressed Regenerate /
     * picked an event month, the GRID is authoritative: they typed the numbers they want, and a
     * generator silently overwriting them would be the "the screen disagrees with what Save
     * writes" defect s1 and s2b were paid for. The losing generator is discarded with no side
     * effect — `reamortVia` stays null, so the conservation console.warn does not fire for a
     * draft; the panel's Σ readout and the affirm gate are its readers instead.
     * ⛔ B1-B6 BELOW ARE UNTOUCHED, AND SO IS THE onSave PAYLOAD — there is still exactly ONE
     * payload literal in this file. The only textual change to the chain is the `else` on the
     * next line. This is Phase 0's A3-a (the ADDITIVE 7th branch), chosen over A3-b (rewriting
     * the chain into generators-that-write) precisely so the capability ships without
     * re-proving the six branches s2b stabilised — §6 Mantra 6, scaffolding before switchover. */
    if (draftAmort) amort = draftAmort;
    else if (f.eventBooking && f.eventMonth) {
      const booksHistorically = !!priorAmort && Object.keys(priorAmort).some(m => m < firstForecast);
      if (f.eventMonth < firstForecast || booksHistorically) {
        const msg = f.eventMonth < firstForecast
          ? `${formatMonthLabel(f.eventMonth, "shortYear")} is on or before the close month, so it is a CLOSED period. Closed bookings will be preserved and only the forecast will change. Continue?`
          : `This contract already books in closed months. Those bookings will be preserved and only the forecast will change. Continue?`;
        if (!window.confirm(msg)) return null;   // operator declined — nothing is written
      }
      const lagToEvent = monthOffset(f.start, f.eventMonth);
      amort = buildForecastTail(priorAmort || {}, f.prepaid, f.start, 1, lagToEvent, firstForecast);
      reamortVia = "event booking";
    }
    else if (f.indefinite) amort = {};
    // E (amort-lag): create / explicit full-regen → straight-line from the lagged
    // start (start + lag months); the amount parks on the BS until amort begins.
    /* INITIAL GENERATION — a contract with NO schedule yet. Nothing to preserve, so the
     * full straight-line rebuild is correct and is UNCHANGED. */
    else if (!priorAmort) amort = buildCycleAmort(addMonths(f.start, lag), f.length, f.prepaid);
    /* ★★ C1 s2b (decision 1b) — REGENERATE IS NOW A *SAFE FORECAST-ONLY RESET*.
     * It used to share the line above: buildCycleAmort(start+lag, length, amount) — a
     * full rebuild that IGNORES history and re-spreads the entire amount as if nothing
     * had been booked. On a contract WITH history that discards closed months, which is
     * why the Step-6 checklist banner used to point operators at the one button that
     * would destroy the imported schedule it was flagging (fixed in s1; this fixes the
     * button itself). Now it means "reset the FUTURE to the formula": every cell <
     * firstForecast is preserved and only the forecast portion is rebuilt from the
     * terms, via the same primitive branches 1 and 4 use. Manual forecast edits are
     * discarded — which is the point of a reset — and closed history never is. */
    else if (regenRequested) { amort = buildForecastTail(priorAmort, f.prepaid, f.start, f.length, lag, firstForecast); reamortVia = "Regenerate"; }
    // Lag changed on an EXISTING contract → re-amort FORECAST ONLY (m >= firstForecast),
    // preserving every closed/historical cell. The remaining balance spreads over the
    // lag-aware forecast window so Σ stays = prepaid. NEVER the full rebuild above
    // (that wipes historical given-data). [Phase 0 C4]
    else if (lagChanged) {
      // Build 4c: the tail math is now the SHARED buildForecastTail (prepaidAmort.js) — one matcher
      // with the import generator. Byte-identical to the prior inline logic (proven in verify-4c).
      amort = buildForecastTail(priorAmort, f.prepaid, f.start, f.length, lag, firstForecast);
      reamortVia = "lag change";
    }
    else amort = priorAmort;
    /* ★ C1 hardening (CHANGE 4) — THE CONSERVATION GUARD, now covering all three re-amort
     * branches instead of `lagChanged` alone. The threshold is unchanged ($0.02, which already
     * silences the ~20 cent-level remainder-placement rows per tenant).
     * ⓘ A residual here is no longer necessarily a fault: the primitive's directional floor
     * deliberately books $0 forward on an over-booked contract, so Σ legitimately equals
     * Σ(closed) and the difference IS the over-booking. The message says which direction, so a
     * reader can tell an over-booking from a shortfall without opening the row. The Stage-3
     * grid will give this a visible reader; until then it is at least no longer blind to the
     * two paths that were destroying schedules. */
    if (reamortVia) {
      const tot = r2(Object.values(amort || {}).reduce((s, v) => s + Number(v), 0));
      const want = r2(Number(f.prepaid) || 0);
      if (Math.abs(tot - want) > 0.02) {
        const dir = tot > want ? "over-booked history (floored forward)" : "unplaced balance";
        console.warn(`prepaid re-amort via ${reamortVia}: Σ ${tot} ≠ prepaid ${want} — ${dir} ${r2(tot - want)} (id ${contract?.id})`);
      }
    }
    onSave({
      vendor_name: f.vendorName.trim(),
      vendor_id: f.vendorId ?? null,
      account: f.account.trim(),         // the human-readable label (display/legacy/overlay)
      /* ★ NEVER SEND AN EMPTY SLUG OVER A STORED ONE. If the form's slug is blank but the
       * loaded contract had one, send the stored value — a re-save of a screen that could
       * not resolve its account must not DESTROY the routing that screen failed to show.
       * The server holds the same guard (COALESCE-on-blank); this is the client half, and
       * §12.5 rule 16 is why the server one is the real one. */
      is_line: f.isLine || (contract && contract.is_line) || null,
      start_month: f.start,
      length_months: f.indefinite ? null : Number(f.length),
      prepaid_amount: r2(Number(f.prepaid) || 0),
      amort,
      // Event Name is null whenever the field is hidden (no stray value).
      event_identifier: (showEvent && f.eventIdentifier.trim()) ? f.eventIdentifier.trim() : null,
      // cat-2 D1: event-month marker — set only on explicit opt-in + chosen month.
      event_month: (f.eventBooking && f.eventMonth) ? f.eventMonth : null,
      /* ★★ P3-B item 3 — THE PERSISTED LAG IS DERIVED FROM THE DICT BEING PERSISTED.
       *
       * WHAT THIS USED TO BE: `amort_lag_months: lag` — the operator's TYPED `f.amortLag`,
       * written independently of the `amort` above it. This was the LAST of six write
       * sites still doing that; the wizard host (AdminPage, the Step-6 import payload and
       * step6PrepaidFromModal) and the shipped `prepaidReconcile` (its partial patch and
       * its orphan re-add) all derive. ONE rule, all callers — the §12.5 `billing_anchor`
       * shape, where a single source of truth still produced two answers because two
       * callers disagreed about what to hand it.
       *
       * ⓘ NORMALLY A NO-OP, AND THAT IS THE POINT: the dict was generated USING the typed
       *   lag, so deriving from it recovers the same number. MEASURED on live data:
       *   stored == derived on 680/680 contracts across all four tenants.
       * ★ IT DIVERGES ONLY WHERE THE DICT AND THE KEYSTROKE GENUINELY DISAGREE, and there
       *   the DICT MUST WIN — it is what the database will hold:
       *     · the `lagChanged` arm re-amortizes the FORECAST and preserves history, so the
       *       first key stays historical and the schedule still starts at the OLD offset
       *       while the box shows the new one;
       *     · a grid draft pins arbitrary cells;
       *     · `indefinite` empties the dict to {} while a non-zero lag sits in the field.
       *   A stored lag that does not describe the stored schedule is not cosmetic: the next
       *   Regenerate is `buildCycleAmort(addMonths(start, lag), …)`, which rebuilds from it.
       * ⛔ SAFE FOR THE EVENT CLASS BY CONSTRUCTION, NOT BY LUCK: `deriveAmortLag` returns
       *   0 whenever `isPrepaidEventStyle` is true, and that predicate reads vendor_name /
       *   conference_name / event_identifier — all present on this payload. So a
       *   Conferences row keeps its 0 instead of gaining the 1-13 month offset its single
       *   cell would otherwise imply. Do NOT "simplify" that carve-out: without it all 19
       *   event rows per tenant re-derive to a non-zero lag. */
      amort_lag_months: deriveAmortLag({
        vendor_name: f.vendorName.trim(),
        /* NULL, and that is CORRECT rather than a shortcut: `conference_name` is a
         * TRANSIENT parser field (parseVendorExcel: "NOT a DB column and never
         * persisted"), so no stored contract can carry one and this modal has no such
         * input. The carve-out still fires for every live event row via `vendor_name ===
         * 'Conferences'` — measured: 19 rows per tenant, event_identifier NULL on all of
         * them. Passing a field no producer supplies would only imply one exists. */
        conference_name: null,
        event_identifier: (showEvent && f.eventIdentifier.trim()) ? f.eventIdentifier.trim() : null,
        start_month: f.start,
        length_months: f.indefinite ? null : Number(f.length),
        prepaid_amount: r2(Number(f.prepaid) || 0),
        amort,
      }),
      /* Prepaid renewals part 2: the Renews answer travels only when the contract can renew (no "—"); otherwise the key is
       * absent and the host keeps what is stored (the PUT writes renews only when the body carries it, D2). */
      ...(renewsDash ? {} : { renews: f.renews === "yes" ? true : f.renews === "no" ? false : null }),
    }, contract ? contract.id : null);
  };

  // ── SC-4 recurring generator (create-only). PAY_FREQ + addMonths are module-scope.
  const freqMo = PAY_FREQ.find(pp => pp.id === f.freq)?.months || 12;
  // (a local monthsBetween lived here, ALREADY dead in VendorDashboard — its only occurrence was its own declaration. Dropped.)
  // Cycle starts: one per cycle-start ≤ end (default = lastForecast horizon); never
  // exceeds the horizon (the end dropdown is capped at lastForecast).
  const recurBound = f.end || lastForecast || "";
  const recurStarts = (() => {
    if (!f.recurring) return [];
    const out = []; let cs = f.start;
    while (cs && recurBound && cs <= recurBound) { out.push(cs); cs = addMonths(cs, freqMo); }
    return out;
  })();
  const recurringOk = (f.vendorId != null) && f.isLine && f.start && f.freq && (Number(f.prepaid) || 0) > 0 && recurStarts.length > 0;
  const handleGenerate = () => {
    const amount = r2(Number(f.prepaid) || 0);
    // E (amort-lag): integer month offset, applied (and PERSISTED) on every cycle so
    // each generated renewal carries the lag forward (the inheritance mechanism).
    const lag = Number(f.amortLag) || 0;
    const rows = recurStarts.map(cs => {
      /* ★ P3-B item 3 — same rule on the generator path: the persisted lag is DERIVED from
       * the cycle dict built one line above, not read off the input box.
       * ⓘ HERE IT IS A NO-OP BY CONSTRUCTION AND WILL STAY ONE — `buildCycleAmort` starts
       *   the schedule at `start + lag`, so the first key IS `start + lag` and
       *   `deriveAmortLag` reads exactly the `lag` that produced it. Deriving anyway is
       *   what makes the guarantee hold if that generator ever changes: the column would
       *   follow the schedule instead of silently describing the old one.
       * ⓘ `event_identifier: null` and a non-Conferences vendor mean the carve-out does not
       *   fire on this path — a recurring series is never an event booking (the field below
       *   says so), so the derived value is the straight offset. */
      /* 2026-10-29 (prepaid renewals, part 1): the cycle row is built by the ONE primitive the renewal generator also
       * calls (engines/prepaidAmort.js prepaidCycleRow) -- the same fields in the same order: vendor_id REQUIRED (the
       * pre-selected vendor), account = the label, is_line = the SC-2 slug section 12b routes by, event_month null (a
       * recurring series is never an event booking). Output byte-identical to the inline build it replaces
       * (diffs/prepaid-renews-p1-build.txt V4). */
      return prepaidCycleRow({
        vendorName: f.vendorName.trim(), vendorId: f.vendorId, account: f.account.trim(), isLine: f.isLine,
        start: cs, lengthMonths: freqMo, amount, lag,
      });
    });
    if (rows.length === 0) return;
    if (rows.length > 24 && !window.confirm(`This will create ${rows.length} prepaid contracts — continue?`)) return;
    onGenerate(rows);
  };

  return (
        <GuardedDialog isDirty={isDirty} onClose={onClose} note={null} cardStyle={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.1)",borderRadius:"20px",padding:"32px",width:"560px",maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"24px"}}>
          <h3 style={{margin:0,fontSize:"20px",fontWeight:700,color:"#1A1C2E"}}>{isNew?"Add Prepaid Contract":"Edit Prepaid Contract"}{isDirty && <span style={{color:"#E67E22",fontWeight:700,marginLeft:"6px"}}>*</span>}</h3>
        </div>
        <ViewOnly canEdit={canEdit}><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px"}}>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lS}>Vendor{isNew && <span style={{color:"#C0392B"}}> *</span>}</label>
            <select style={iS} value={f.vendorId ?? ""} onChange={e=>{
              const val = e.target.value;
              if (val === "") setF(p=>({...p, vendorId:null}));
              else { const v = (vendors||[]).find(x=>String(x.id)===val); if (v) setF(p=>{
                // default account+slug from the vendor on create, or if still blank.
                const takeDefault = isNew || !p.isLine;
                const defSlug = takeDefault ? (v.isCat || p.isLine) : p.isLine;
                const defAcct = takeDefault ? (acctLabelFor(v) || p.account) : p.account;
                return {...p, vendorId:v.id, vendorName:v.name, account:defAcct, isLine:defSlug};
              }); }
            }}>
              <option value="">{isNew ? "Select a vendor…" : "— unlinked (pick to relink) —"}</option>
              {/* U9 (2026-10-30): each vendor with its IS line (qualified by section where a label repeats -- accountOptions) so duplicate names can be told apart; by name, then line. */}{(vendors||[]).map(v=>({v,line:vendorLineDisplay(v)})).sort((a,b)=>compareNamesNaturally(a.v.name,b.v.name)||a.line.localeCompare(b.line)).map(({v,line})=><option key={v.id} value={v.id}>{v.name + " \u2014 " + line}</option>)}
            </select>
            <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Each prepaid contract is its own row, renewals included. Answer Renews below: Yes creates the renewals as contracts through the forecast.</div>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lS}>Account</label>
            <select style={iS} autoFocus={!!prelinkVendor} value={f.isLine} onChange={e=>{
              const slug=e.target.value;
              const opt=accountOptions.find(o=>o.value===slug);
              setF(p=>({...p, isLine:slug, account: opt ? opt.label : p.account}));
            }}>
              {!f.isLine && <option value="">Select an account…</option>}
              {accountOptions.map(o=><option key={o.value} value={o.value}>{o.display}</option>)}
            </select>
            <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Defaults to the vendor's GL account; override for prepaid on a different line. Lines sharing a name are tagged by category.</div>
          </div>
          {isNew && (
            <div style={{gridColumn:"1/-1"}}>
              <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",fontSize:"13px",fontWeight:600,color:"#2E86C1"}}>
                <input type="checkbox" checked={f.recurring} onChange={e=>setF(p=>({...p, recurring:e.target.checked}))}/>
                Recurring series — generate one contract per cycle through the forecast horizon
              </label>
              {f.recurring && <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Stamps an independent stored contract for each cycle (start → "generate through"). Each is individually editable / deletable.</div>}
            </div>
          )}
          <div>
            <label style={lS}>Start Month</label>
            <select style={iS} value={f.start} onChange={e=>setF(p=>({...p, start:e.target.value}))}>
              {!monthOptions.includes(f.start) && f.start && <option value={f.start}>{formatMonthLabel(f.start, "shortYear")}</option>}
              {monthOptions.map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
            </select>
          </div>
          <div>
            <label style={lS}>{f.recurring ? "Frequency" : "Length"}</label>
            {f.recurring ? (
              <select style={iS} value={f.freq} onChange={e=>setF(p=>({...p, freq:e.target.value}))}>
                {PAY_FREQ.map(pp=><option key={pp.id} value={pp.id}>{pp.label}</option>)}
              </select>
            ) : (<>
              {/* ★ D6 — the months INPUT is the source of truth for length_months.
                * NumInput pattern (§5): text + inputMode numeric, parseInt on commit, select()
                * on focus. The `val===""?p.length:…` guard shape is KEPT from the dropdown so an
                * emptied field can never write NaN into `length` (it holds the prior value until
                * a real number is typed; `requiredOk` still gates a save on > 0).
                * ★ DECISION 4 — disabled while a grid draft is active, mirroring D5's Reset
                * guard: B0 makes the draft win, so a term change would shape nothing until the
                * edits are discarded. Same reason, same remedy, same wording. */}
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <input style={{...iS,opacity:(f.indefinite||draftAmort)?0.5:1,width:"84px",textAlign:"right"}}
                  disabled={f.indefinite || !!draftAmort} type="text" inputMode="numeric"
                  value={f.indefinite ? "" : String(f.length ?? "")}
                  onChange={e=>{const val=e.target.value.replace(/[^0-9]/g,""); setF(p=>({...p, length: val===""?p.length:Math.min(parseInt(val,10),LEN_MAX)}));}}
                  onFocus={e=>e.target.select()} placeholder="12"/>
                <span style={{fontSize:"12px",color:"#6B7094"}}>months</span>
              </div>
              {/* The cadence words survive as SHORTCUTS — one tap for the four standard terms,
                  with no privileged status in the data model. */}
              <div style={{display:"flex",gap:"4px",marginTop:"6px",flexWrap:"wrap"}}>
                {PERIODS.map(([n,lab])=>(
                  <button key={n} type="button" disabled={f.indefinite || !!draftAmort}
                    onClick={()=>setF(p=>({...p, length:n}))}
                    style={{padding:"3px 8px",borderRadius:"6px",fontSize:"10px",fontWeight:600,fontFamily:"'DM Sans',sans-serif",
                      cursor:(f.indefinite||draftAmort)?"not-allowed":"pointer",
                      border:Number(f.length)===n?"1px solid #2E86C1":"1px solid rgba(0,0,0,0.15)",
                      background:Number(f.length)===n?"rgba(46,134,193,0.08)":"#fff",
                      color:(f.indefinite||draftAmort)?"#B0B3C8":(Number(f.length)===n?"#2E86C1":"#6B7094")}}>{lab}</button>
                ))}
              </div>
              {!f.indefinite && lenCaption && (
                <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>{lenCaption.toLowerCase()}</div>
              )}
              {!!draftAmort && !f.indefinite && (
                <div style={{fontSize:"10px",color:"#8A6D00",marginTop:"4px",fontStyle:"italic"}}>{"Discard your cell edits first — the grid is showing unsaved changes."}</div>
              )}
              <label style={{display:"flex",alignItems:"center",gap:"6px",marginTop:"6px",fontSize:"11px",color:"#6B7094",cursor:"pointer"}}>
                <input type="checkbox" checked={f.indefinite} onChange={e=>toggleIndefinite(e.target.checked)}/>
                No fixed length (indefinite)
              </label>
              {f.indefinite && <div style={{fontSize:"10px",color:"#2E86C1",marginTop:"4px",fontStyle:"italic"}}>Parks the full amount on the BS; no amortization until drawn down.</div>}
            </>)}
          </div>
          <div>
            <label style={lS}>{f.recurring ? "Prepaid Amount (per cycle)" : "Prepaid Amount"}</label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:"12px",top:"50%",transform:"translateY(-50%)",color:"#8B8FA3",fontSize:"14px",pointerEvents:"none"}}>$</span>
              <input style={{...iS,paddingLeft:"24px"}} type="text" inputMode="decimal"
                value={amtDraft !== null ? amtDraft : (f.prepaid ? fmtAmount(f.prepaid) : "")}
                onChange={e=>{const t=amountText(e.target.value);setAmtDraft(t);setF(p=>({...p, prepaid:amountValue(t)}));}}
                onFocus={e=>{setAmtDraft(f.prepaid ? fmtAmount(f.prepaid) : "");const el=e.target;setTimeout(()=>el.select(),0);}}
                onBlur={()=>setAmtDraft(null)} placeholder="0"/>
            </div>
          </div>
          {/* E (amort-lag): deferred amortization start as an integer month offset.
              Shown on create AND edit (replaces the old create-only month-picker). */}
          {!f.indefinite && !f.eventBooking && (
            <div>
              <label style={lS}>Amortization Lag (months)</label>
              <input style={iS} type="text" inputMode="numeric"
                value={f.amortLag === "" ? "" : String(f.amortLag)}
                onChange={e=>{const raw=e.target.value.replace(/[^0-9]/g,"");setF(p=>({...p, amortLag: raw===""?0:parseInt(raw,10)}));}}
                onFocus={e=>e.target.select()} placeholder="0"/>
              <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Months to defer amortization past the payment month; the amount parks on the BS until then.{lagChanged && " Re-amortizes forecast months only; closed months are preserved."}</div>
            </div>
          )}
          {/* SC-4: recurring end-of-series month — default = horizon, capped at horizon. */}
          {isNew && f.recurring && (
            <div>
              <label style={lS}>Generate through</label>
              <select style={iS} value={f.end} onChange={e=>setF(p=>({...p, end:e.target.value}))}>
                <option value="">Forecast horizon ({lastForecast ? formatMonthLabel(lastForecast, "shortYear") : "—"})</option>
                {monthOptions.filter(m=>m>=f.start && (!lastForecast || m<=lastForecast)).map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
              </select>
              <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>A cycle is created for each start ≤ this month. Default = horizon; can't exceed it.</div>
            </div>
          )}
          {isNew && f.recurring && (
            <div style={{gridColumn:"1/-1",fontSize:"11px",fontWeight:600,color:recurStarts.length?"#27AE60":"#C0392B"}}>
              {recurStarts.length
                ? `Will create ${recurStarts.length} contract${recurStarts.length===1?"":"s"} (cycle starts: ${recurStarts.slice(0,6).map(m => formatMonthLabel(m, "shortYear")).join(", ")}${recurStarts.length>6?", …":""}).`
                : "No cycles in range — pick a later “generate through” month or an earlier start."}
            </div>
          )}
          {showEvent && !f.recurring && (
            <div style={{gridColumn:"1/-1"}}>
              <label style={lS}>Event Name <span style={{textTransform:"none",fontWeight:400,color:"#8B8FA3"}}>(optional)</span></label>
              <input style={iS} value={f.eventIdentifier} onChange={e=>setF(p=>({...p, eventIdentifier:e.target.value}))}/>
              <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Distinguishes conference registrations that share the same date and amount.</div>
            </div>
          )}
          {/* cat-2 D1: explicit event-month booking opt-in (Conferences section, single contract). */}
          {!f.indefinite && !f.recurring && (
            <div style={{gridColumn:"1/-1"}}>
              <label style={{display:"flex",alignItems:"center",gap:"6px",fontSize:"11px",color:"#6B7094",cursor:"pointer"}}>
                <input type="checkbox" checked={f.eventBooking} onChange={e=>setF(p=>({...p, eventBooking:e.target.checked}))}/>
                Event booking <span style={{color:"#8B8FA3"}}>(books the full amount in the event month, not straight-line)</span>
              </label>
              {f.eventBooking && (
                <div style={{marginTop:"8px"}}>
                  <label style={lS}>Event Month</label>
                  <select style={iS} value={f.eventMonth} onChange={e=>setF(p=>({...p, eventMonth:e.target.value}))}>
                    <option value="">Select event month…</option>
                    {monthOptions.map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
                  </select>
                  <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>The full prepaid amount parks on the BS until this month, then books entirely here.</div>
                </div>
              )}
            </div>
          )}
          {/* Prepaid renewals part 2 (Brian 10-29): the Renews answer on a single contract. Yes = the renewals are created as
              contracts through the forecast; No = none (and unedited renewals already created are removed). Unanswered shows the
              suggestion, marked (12.5 rule 92). A contract that cannot renew shows "—". */}
          {!f.recurring && (
            <div style={{gridColumn:"1/-1"}} data-renews-field="1">
              <label style={lS}>Renews</label>
              {renewsDash ? (
                <div style={{fontSize:"12px",color:"#8B8FA3"}} title={"No renewal: " + renewsDash}>{"—"} <span style={{fontSize:"10px"}}>({renewsDash} does not renew)</span></div>
              ) : (<>
                <select style={iS} value={f.renews} aria-label="Renews" onChange={e=>setF(p=>({...p, renews:e.target.value}))}>
                  {f.renews === "" && <option value="">Not answered</option>}
                  <option value="yes">Yes {"—"} create the renewals through the forecast</option>
                  <option value="no">No {"—"} it does not renew</option>
                </select>
                {f.renews === "" && renewsSuggestion && (
                  <div style={{fontSize:"10px",color:"#8A5B00",marginTop:"4px"}} title={renewsSuggestion.reason}>
                    {"Suggested: " + (renewsSuggestion.suggest ? "Yes" : "No") + " — confirm or change (" + renewsSuggestion.reason + ")"}
                  </div>
                )}
              </>)}
            </div>
          )}

          {keyChanged && !regenRequested && (
            <div style={{gridColumn:"1/-1",padding:"10px 12px",borderRadius:"8px",background:"rgba(212,172,13,0.06)",border:"1px solid rgba(212,172,13,0.25)"}}>
              <div style={{fontSize:"11px",color:"#8A6D00"}}>Amortization schedule was entered/imported and is preserved; it may no longer match the new amount/length.</div>
              {/* ★★ C1 s3 · INC 4 / D5 — THE LABEL FINALLY DESCRIBES THE OPERATION.
                * Since s2b, branch B4 is `buildForecastTail(...)`: every cell < firstForecast is
                * PRESERVED and only the forecast portion is rebuilt from the terms. The button
                * still said "Regenerate schedule" and the confirmation "Schedule will be
                * regenerated straight-line" — both of which sound TOTAL. The Step-6 banner was
                * corrected by s1 and already says it properly, so the wizard and this modal have
                * been describing the SAME operation differently ever since. That is the s1
                * defect-3 shape one layer in: a signal describing an operation rather than its
                * outcome. Strings only — B4 is untouched and byte-identical.
                *
                * ★ AND THE ACTIVE-DRAFT GUARD (Phase 0.5 decision 4, new since Increments 2-3).
                * B0 makes a grid draft WIN over every generator, so pressing Reset with pinned
                * cells sets `regenRequested` and then changes nothing at all — a control that
                * looks like it worked and did not. Disabling it is a UI guard ONLY: B0 and B4
                * keep their exact behaviour, and the button explains itself rather than failing
                * silently. Discarding the cell edits re-enables it with no other state to undo. */}
              <button onClick={()=>setRegenRequested(true)} disabled={!!draftAmort}
                title={draftAmort ? "The grid is showing unsaved cell edits, which take precedence over the formula." : undefined}
                style={{marginTop:"8px",padding:"6px 12px",borderRadius:"8px",border:"1px solid rgba(212,172,13,0.4)",background:"#fff",color:draftAmort?"#B0B3C8":"#8A6D00",cursor:draftAmort?"not-allowed":"pointer",fontSize:"12px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Reset forecast to formula</button>
              {draftAmort && (
                <div style={{fontSize:"10px",color:"#8A6D00",marginTop:"6px",fontStyle:"italic"}}>{"Discard your cell edits first — the grid is showing unsaved changes."}</div>
              )}
            </div>
          )}
          {regenEndShift && (
            <div data-prepaid-regen-end="" style={{gridColumn:"1/-1",padding:"10px 12px",borderRadius:"8px",background:"rgba(212,172,13,0.06)",border:"1px solid rgba(212,172,13,0.25)",fontSize:"11px",color:"#8A6D00"}}>
              {"\u26A0 This rebuilds the forecast from a lag of " + regenEndShift.lag + " month" + (regenEndShift.lag === 1 ? "" : "s") + ", so the schedule would end in " + formatMonthLabel(regenEndShift.will, "shortFull") + " instead of " + formatMonthLabel(regenEndShift.was, "shortFull") + ". The lag that is saved is read from the schedule itself, not from this box. If this contract's amortization started late (a catch-up), cancel and edit its forecast months directly instead."}
            </div>
          )}
          {regenRequested && !f.indefinite && !termEndedNoop && (
            <div style={{gridColumn:"1/-1",fontSize:"11px",color:"#27AE60"}}>Forecast months will be reset to the straight-line formula on save. Closed months are preserved.</div>
          )}
          {/* ★ C1 hardening (CHANGE 5) — the NAMED handled state. It replaces the green
              "will be regenerated" line rather than sitting beside it, because on this row
              that line would be false. */}
          {termEndedNoop && (
            <div style={{gridColumn:"1/-1",padding:"10px 12px",borderRadius:"8px",background:"rgba(212,172,13,0.06)",border:"1px solid rgba(212,172,13,0.25)",fontSize:"11px",color:"#8A6D00"}}>
              {"This contract's amortization term ended before the forecast starts, so there is nothing to regenerate forward — the existing schedule is kept exactly as it is. To move or re-spread it, edit the months directly."}
            </div>
          )}

          {/* ★★ C1 s3 · GRID INCREMENT 1 — the read-only panel. See the derivation block above
              for why each mechanic is what it is. Rendered on EDIT only: a contract that does
              not exist yet has no saved schedule to describe. */}
          {contract && (
            <div style={{gridColumn:"1/-1",marginTop:"4px",padding:"14px",borderRadius:"10px",background:"#F7F8FA",border:"1px solid rgba(0,0,0,0.08)"}}>
              <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:"12px",flexWrap:"wrap",marginBottom:"10px"}}>
                <div style={{fontSize:"11px",fontWeight:700,color:"#1A1C2E",textTransform:"uppercase",letterSpacing:"0.5px"}}>Saved amortization schedule</div>
                <div style={{fontSize:"10px",color:sched.isDraft?"#8A6D00":"#8B8FA3",fontStyle:"italic"}}>
                  {sched.isDraft
                    ? "Edited — showing what Save will write, not what is stored."
                    : "Every month is editable."}
                </div>
              </div>
              {sched.months.length === 0 ? (
                <div style={{fontSize:"11px",color:"#6B7094"}}>{"No amortization months are saved for this contract — the full amount sits on the balance sheet."}</div>
              ) : (<>
                <div style={{overflowX:"auto",border:"1px solid rgba(0,0,0,0.08)",borderRadius:"8px",background:"#FFFFFF"}}>
                  <table style={{borderCollapse:"collapse",minWidth:"100%"}}>
                    <thead>
                      <tr>
                        {sched.seamOn ? (<>
                          {sched.closed.length > 0 && <th colSpan={sched.closed.length} style={{...gHead,color:"#2A6DB0"}}>Actual (GL)</th>}
                          {sched.fc.length > 0 && <th colSpan={sched.fc.length} style={{...gHead,color:"#1A1C2E",borderLeft:sched.seamIdx >= 0 ? "2px solid #1A1C2E" : "none"}}>Forecast</th>}
                        </>) : (
                          <th colSpan={sched.months.length} style={{...gHead,color:"#1A1C2E"}}>{"Schedule (close month unavailable — no split shown)"}</th>
                        )}
                      </tr>
                      <tr>
                        {sched.months.map((m,i)=>(
                          <th key={m} style={{...gCell,fontWeight:600,background:"#F0F1F5",color:(sched.seamOn && m < firstForecast)?"#2A6DB0":"#1A1C2E",borderLeft:i===sched.seamIdx?"2px solid #1A1C2E":undefined}}>{formatMonthLabel(m, "shortYear")}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {sched.months.map((m,i)=>{
                          const v = sched.val(m);
                          const isClosed = sched.seamOn && m < firstForecast;
                          const tdS = {...gCell,borderTop:"1px solid rgba(0,0,0,0.06)",borderLeft:i===sched.seamIdx?"2px solid #1A1C2E":undefined};
                          // ★ INC 3 — ONE input for BOTH halves. Increment 2 rendered closed
                          // cells as plain text; they are now editable on the same handlers,
                          // differing only in colour (§5: historical blue vs forecast black).
                          // Keeping a single code path is deliberate — two near-identical cell
                          // renderers is exactly the divergence Principle 0 warns about.
                          const pinned = sched.pinned.has(m);
                          const shown = (cellDraft && cellDraft.m === m) ? cellDraft.text : (v === null ? "" : fmtCell(v));
                          return (
                            <td key={m} style={{...tdS,padding:"3px 4px"}}>
                              <input
                                style={{...gInp,
                                  color:isClosed?"#2A6DB0":"#1A1C2E",
                                  fontWeight:pinned?700:400,
                                  borderColor:pinned?"#8A6D00":"rgba(0,0,0,0.15)",
                                  background:pinned?"rgba(212,172,13,0.08)":"#FFFFFF"}}
                                type="text" inputMode="decimal" value={shown}
                                title={pinned
                                  ? "Edited — pinned. The unedited forecast cells re-spread around it."
                                  : (isClosed
                                      ? "Closed month (GL actual). Editing it records what the ledger booked and re-prices the forecast; it does not change the closed statements."
                                      : "Forecast month — type to pin this cell.")}
                                onFocus={e=>{ setCellDraft({ m, text: v === null ? "" : String(v) }); setTimeout(()=>e.target.select(),0); }}
                                onChange={e=>setCellDraft({ m, text: e.target.value })}
                                onBlur={e=>commitCell(m, e.target.value)}
                                onKeyDown={e=>{ if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setCellDraft(null); e.target.blur(); } }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* THE TWO HALVES ARE DIFFERENT KINDS OF NUMBER and the operator has no other
                    way to learn it — so the panel says so rather than relying on a tint. */}
                <div style={{display:"flex",gap:"16px",flexWrap:"wrap",marginTop:"8px",fontSize:"10px",lineHeight:1.5}}>
                  {sched.closed.length > 0 && <div style={{flex:"1 1 230px",color:"#2A6DB0"}}><b>Actual (GL)</b>{" — recorded history; does not change the closed statements."}</div>}
                  {sched.fc.length > 0 && <div style={{flex:"1 1 230px",color:"#1A1C2E"}}><b>Forecast</b>{" — drives the Income Statement and Balance Sheet."}</div>}
                </div>
                {/* ★★ INC 3 — THE STANDING RULE, stated in full on the panel because it is the
                    rule the operator is implicitly agreeing to every time they type in a closed
                    cell (Phase 0 §B7's closing instruction). Both halves of it matter: the
                    closed statements do NOT move, and the forecast DOES. */}
                <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"6px",lineHeight:1.5}}>
                  {"Editing a "}<b style={{color:"#2A6DB0"}}>closed</b>
                  {" month records what the general ledger booked. It does "}<b>not</b>
                  {" change the closed income statement, balance sheet or cash flow — those come from the ledger — but it "}<b>does</b>
                  {" re-spread the forecast, so the schedule still sums to the amount paid. A blank cell means no entry is stored for that month; 0.00 means a zero is stored."}
                </div>
                {/* ★ THE Σ READOUT (Phase 0 D7) — the first visible reader for the conservation
                    residual, which until now existed only as a console.warn on one branch. */}
                <div style={{display:"flex",gap:"18px",flexWrap:"wrap",alignItems:"baseline",marginTop:"10px",paddingTop:"10px",borderTop:"1px solid rgba(0,0,0,0.08)",fontSize:"11px",fontFamily:"'Space Mono',monospace"}}>
                  {sched.seamOn && <div><span style={gLbl}>Closed Σ</span><span style={{color:"#2A6DB0"}}>{fmtCell(sched.closedSum)}</span></div>}
                  {sched.seamOn && <div><span style={gLbl}>Forecast Σ</span><span style={{color:"#1A1C2E"}}>{fmtCell(sched.fcSum)}</span></div>}
                  <div><span style={gLbl}>Total Σ</span><span style={{color:"#1A1C2E"}}>{fmtCell(sched.total)}</span></div>
                  <div><span style={gLbl}>Prepaid amount</span><span style={{color:"#1A1C2E"}}>{fmtCell(sched.prepaid)}</span></div>
                  {/* ★ C1 s3 · INC 1.1 — colour + weight come from the derivation, which is
                      where the threshold lives and where the verify can extract them. The
                      JSX is a pure consumer: it renders the signal, it does not decide it. */}
                  <div><span style={gLbl}>Residual</span><span style={{fontWeight:sched.resBold?800:700,color:sched.resColor}}>{fmtCell(sched.residual)}</span></div>
                </div>
                <div style={{fontSize:"10px",marginTop:"6px",lineHeight:1.5,color:sched.msgColor,fontWeight:sched.resBold?700:400}}>
                  {sched.residualMsg}
                </div>
                {/* ★ INC 2 — the draft's own state: what conservation did, and the affirm gate
                    when it could not be done. Both live beside the residual they describe. */}
                {sched.isDraft && (
                  <div style={{marginTop:"8px",paddingTop:"8px",borderTop:"1px dashed rgba(0,0,0,0.12)"}}>
                    <div style={{display:"flex",gap:"10px",alignItems:"baseline",flexWrap:"wrap",fontSize:"10px",color:"#6B7094"}}>
                      <span style={{fontWeight:700,color:"#8A6D00"}}>
                        {sched.pinned.size} edited{sched.pinnedClosed > 0 ? ` (${sched.pinnedClosed} closed)` : ""}
                      </span>
                      <span style={{flex:"1 1 240px"}}>{draftBreaks
                        ? "No unedited forecast month is left to absorb the balance, so the schedule no longer sums to the prepaid amount."
                        : "The unedited forecast months were re-spread so the schedule still sums to the prepaid amount."}</span>
                      <button type="button" onClick={clearGridEdits}
                        style={{padding:"3px 10px",borderRadius:"6px",border:"1px solid rgba(0,0,0,0.15)",background:"#fff",color:"#6B7094",cursor:"pointer",fontSize:"10px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>
                        Discard cell edits
                      </button>
                    </div>
                    {draftBreaks && (
                      <label style={{display:"flex",alignItems:"flex-start",gap:"8px",marginTop:"8px",padding:"8px 10px",borderRadius:"8px",background:"rgba(192,57,43,0.06)",border:"1px solid rgba(192,57,43,0.25)",cursor:"pointer"}}>
                        <input type="checkbox" checked={draftAffirmed} onChange={e=>setDraftAffirmed(e.target.checked)} style={{marginTop:"2px"}}/>
                        <span style={{fontSize:"10px",color:"#C0392B",lineHeight:1.5}}>
                          {`Save anyway with a $${fmtCell(Math.abs(sched.residual))} ${sched.residual > 0 ? "shortfall" : "over-booking"} against the prepaid amount.`}
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </>)}
            </div>
          )}
        </div></ViewOnly>

        {error && (
          <div style={{marginTop:"20px",padding:"12px 16px",borderRadius:"10px",background:"rgba(192,57,43,0.06)",border:"1px solid rgba(192,57,43,0.25)"}}>
            <div style={{fontSize:"12px",color:"#C0392B",fontWeight:600}}>{error.message || "Save failed."}</div>
          </div>
        )}

        <div style={{display:"flex",gap:"12px",marginTop:"28px",justifyContent:"flex-end",alignItems:"center"}}>
          {/* ★ C1 s1 (H): in LOCAL mode the standing message says where the real write is,
              because the control that performs it lives on the screen behind this modal.
              In PERSIST mode the copy is unchanged. */}
          {isDirty
            ? <div style={{flex:1,fontSize:"11px",color:"#8B8FA3",fontStyle:"italic",marginRight:"12px"}}>Unsaved changes — click outside won't close.</div>
            : isLocal
              ? <div style={{flex:1,fontSize:"11px",color:"#8B8FA3",fontStyle:"italic",marginRight:"12px"}}>Changes are applied to the wizard — nothing reaches the database until Save Progress.</div>
              : null}
          {/* ★ C1 s1 (H): the confirm must not assert a deletion that has not happened.
              In local mode the row leaves the wizard list; the DB row survives until the
              Step-6 import runs. */}
          {!isNew && onDelete && <ViewOnly canEdit={canEdit}><button onClick={()=>{ if(window.confirm(isLocal ? "Remove this prepaid contract from the wizard? It is deleted from the database when Step 6 is imported." : "Delete this prepaid contract?")) onDelete(contract.id); }} style={{padding:"10px 18px",borderRadius:"10px",border:"1px solid rgba(192,57,43,0.25)",background:"transparent",color:"#C0392B",cursor:"pointer",fontSize:"13px",fontWeight:600,marginRight:"auto",fontFamily:"'DM Sans',sans-serif"}}>Remove</button></ViewOnly>}
          <button onClick={onClose} style={{padding:"10px 22px",borderRadius:"10px",border:"1px solid rgba(0,0,0,0.12)",background:"transparent",color:"#6B7094",cursor:"pointer",fontSize:"13px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Cancel</button>
          {f.recurring
            ? <ViewOnly canEdit={canEdit}><button disabled={!recurringOk} onClick={handleGenerate} style={{padding:"10px 28px",borderRadius:"10px",border:"none",background:!recurringOk?"#B0B3C8":"linear-gradient(135deg,#27AE60,#2ECC71)",color:"#fff",cursor:recurringOk?"pointer":"not-allowed",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(46,134,193,0.3)"}}>Generate Series</button></ViewOnly>
            : (() => {
                /* ★ INC 2 — BREAK-ON-AFFIRM, not block-on-invalid. `saveOk` withholds the click
                 * ONLY while a non-conserving grid draft is unacknowledged, and the checkbox
                 * that satisfies it sits next to the residual it names, one click away, always
                 * available. Nothing is ever refused: there is no state the operator can reach
                 * from which the save is unreachable. A pre-existing residual on an unedited
                 * row cannot reach this at all (`draftBreaks` requires an active draft). */
                const saveOk = requiredOk && !(draftBreaks && !draftAffirmed);
                const label = isLocal ? "Apply" : "Save";
                return <ViewOnly canEdit={canEdit}><button disabled={!saveOk} onClick={handleSave} title={draftBreaks && !draftAffirmed ? "Tick the acknowledgement above to save a schedule that does not sum to the prepaid amount." : undefined} style={{padding:"10px 28px",borderRadius:"10px",border:"none",background:!saveOk?"#B0B3C8":(isDirty?"linear-gradient(135deg,#27AE60,#2ECC71)":"linear-gradient(135deg,#2E86C1,#3498DB)"),color:"#fff",cursor:saveOk?"pointer":"not-allowed",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(46,134,193,0.3)"}}>{draftBreaks ? `${label} anyway` : label}</button></ViewOnly>;
              })()}
        </div>
    </GuardedDialog>
  );
}
